import React, { useState } from "react";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/core/macro";
import { setHours, setMilliseconds, setMinutes, setSeconds } from "date-fns";
import ActionButton from "../ActionButton.tsx";
import Container from "../Container.tsx";
import ContainerHeader from "../ContainerHeader.tsx";
import FormField from "../FormField.tsx"; // Import the new FormField component
import TimeInputField from "../TimeInputField.tsx";
import DateInputField from "../DateInputField.tsx";
import { Blocker } from "../../api/scheduleApi.ts";
import TextInputField from "../TextInputField.tsx";

interface BlockerFormProps {
  onAddBlocker: (blocker: Blocker) => void;
  existingBlocker: Blocker[] | undefined;
  isLoading: boolean;
}

const generateTimeOptions = (limit: number) => {
  return Array.from({ length: limit }, (_, i) => String(i).padStart(2, "0"));
};

const hours = generateTimeOptions(24);
const minutes = generateTimeOptions(60);

const BlockerForm: React.FC<BlockerFormProps> = ({
  onAddBlocker,
  isLoading,
}) => {
  const { i18n } = useLingui();
  const [title, setTitle] = useState("");
  const [day, setDay] = useState<Date>(new Date());
  const [startHour, setStartHour] = useState("09");
  const [startMinute, setStartMinute] = useState("00");
  const [endHour, setEndHour] = useState("17");
  const [endMinute, setEndMinute] = useState("00");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const startTime = parseInt(startHour) * 60 + parseInt(startMinute);
    const endTime = parseInt(endHour) * 60 + parseInt(endMinute);

    if (endTime <= startTime) {
      setError(i18n._(t`End time must be after start time.`));
      return;
    }

    const from = setMilliseconds(
      setSeconds(
        setMinutes(setHours(day, parseInt(startHour)), parseInt(startMinute)),
        0,
      ),
      0,
    );
    const to = setMilliseconds(
      setSeconds(
        setMinutes(setHours(day, parseInt(endHour)), parseInt(endMinute)),
        0,
      ),
      0,
    );
    const newBlocker: Blocker = {
      id: undefined,
      from,
      to,
      title: title,
    };
    onAddBlocker(newBlocker);
    setTitle("");
    setDay(new Date());
    setStartHour("09");
    setStartMinute("00");
    setEndHour("17");
    setEndMinute("00");
  };

  return (
    <Container className={"mb-4"}>
      <ContainerHeader>{i18n._(t`Add New Blocker`)}</ContainerHeader>
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      >
        <FormField htmlFor="day" label={i18n._(t`Day`)}>
          <DateInputField
            id="startHour"
            value={day}
            onChange={(e) => setDay(new Date(e.target.value))}
            label={""}
          />
        </FormField>

        <FormField htmlFor="startHour" label={i18n._(t`Start Time`)}>
          <div className="flex gap-2">
            <TimeInputField
              id="startHour"
              value={startHour}
              onChange={(e) => setStartHour(e.target.value)}
              options={hours}
            />
            <TimeInputField
              id="startMinute"
              value={startMinute}
              onChange={(e) => setStartMinute(e.target.value)}
              options={minutes}
            />
          </div>
        </FormField>

        <FormField htmlFor="endHour" label={i18n._(t`End Time`)}>
          <div className="flex gap-2">
            <TimeInputField
              id="endHour"
              value={endHour}
              onChange={(e) => setEndHour(e.target.value)}
              options={hours}
            />
            <TimeInputField
              id="endMinute"
              value={endMinute}
              onChange={(e) => setEndMinute(e.target.value)}
              options={minutes}
            />
          </div>
        </FormField>

        <FormField htmlFor="title" label={i18n._(t`Title`)}>
          <TextInputField
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            label={""}
          />
        </FormField>

        <div className="md:col-span-2 lg:col-span-3">
          {error && <p className="mb-2 text-sm text-red-500">{error}</p>}
          <ActionButton
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {i18n._(t`Add Blocker`)}
          </ActionButton>
        </div>
      </form>
    </Container>
  );
};

export default BlockerForm;
